import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    CardMedia,
    Divider,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    CalendarToday as CalendarIcon,
    SportsEsports as GameIcon
} from '@mui/icons-material';
import { getGameImage } from '../../utils/gameImages';

function TimeTrialDetailsModal({ open, timeTrial, onClose }) {
    if (!timeTrial) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case 'upcoming': return 'info';
            case 'active': return 'success';
            case 'finalized': return 'default';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeRemaining = () => {
        if (!timeTrial.end_time || timeTrial.status === 'finalized') return null;
        
        const now = new Date();
        const endDate = new Date(timeTrial.end_time);
        const diff = endDate - now;
        
        if (diff <= 0) return 'Ended';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (days > 0) {
            return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''} remaining`;
        }
        return `${hours} hour${hours !== 1 ? 's' : ''} remaining`;
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            {/* Time Trial Header with Game Image */}
            <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
                <CardMedia
                    component="img"
                    height="250"
                    image={getGameImage({ 
                        name: timeTrial.game_name,
                        background_image: timeTrial.game_background_image
                    })}
                    alt={timeTrial.name}
                    sx={{
                        objectFit: 'cover'
                    }}
                />
                
                {/* Gradient Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.8) 100%)'
                    }}
                />

                {/* Time Trial Name Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 3,
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <TimerIcon sx={{ fontSize: 32 }} />
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontWeight: 700,
                                textShadow: '0 2px 8px rgba(0,0,0,0.5)'
                            }}
                        >
                            {timeTrial.name}
                        </Typography>
                    </Box>
                    
                    {/* Status Chip */}
                    <Chip
                        label={timeTrial.status?.toUpperCase() || 'ACTIVE'}
                        size="medium"
                        color={getStatusColor(timeTrial.status)}
                        sx={{ 
                            fontWeight: 600,
                            bgcolor: alpha('#fff', 0.2),
                            color: 'white',
                            borderColor: alpha('#fff', 0.3)
                        }}
                    />
                </Box>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {/* Time Trial Stats */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Start Date
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDate(timeTrial.start_time)}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                End Date
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDate(timeTrial.end_time)}
                        </Typography>
                    </Box>
                </Box>

                {/* Time Remaining Alert */}
                {getTimeRemaining() && timeTrial.status === 'active' && (
                    <Box 
                        sx={{ 
                            p: 2, 
                            mb: 3, 
                            bgcolor: 'success.main', 
                            color: 'white',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <TrophyIcon />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getTimeRemaining()}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ mb: 3 }} />

                {/* Description */}
                {timeTrial.description && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            About
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {timeTrial.description}
                        </Typography>
                    </Box>
                )}

                {/* Rules */}
                {timeTrial.rules && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Rules
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'background.default',
                                borderRadius: 2,
                                borderLeft: 3,
                                borderColor: 'primary.main'
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {timeTrial.rules}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Game Info */}
                {timeTrial.game_name && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <GameIcon sx={{ fontSize: 20 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Game
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {timeTrial.game_name}
                        </Typography>
                    </Box>
                )}

                {/* Additional Info */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        <strong>Time Trial ID:</strong> {timeTrial.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <strong>Status:</strong> {timeTrial.status?.toUpperCase() || 'ACTIVE'}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button 
                    onClick={onClose} 
                    variant="contained"
                    startIcon={<CloseIcon />}
                    fullWidth
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default TimeTrialDetailsModal;

